/**
 * CourierBookingForm - Complete Courier Booking with Multi-Step Mobile-First Flow
 * 
 * Steps:
 * 1. Pickup Information
 * 2. Delivery Information
 * 3. Package Details
 * 4. Options (Signature, Rush, Multiple Stops)
 * 5. Review & Price Estimate
 * 6. Payment
 * 7. Confirmation
 */
import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin, User, Phone, Clock, Calendar, Package, Truck, Camera,
  ChevronLeft, ChevronRight, Check, AlertTriangle, FileText, Pen,
  Plus, Minus, X, Navigation, DollarSign, Zap, Upload, Info
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { mileageAPI, bookingsAPI } from '../../services/api';

const PACKAGE_TYPES = [
  { value: 'documents', label: 'Documents', icon: FileText },
  { value: 'envelope', label: 'Envelope', icon: FileText },
  { value: 'package', label: 'Package', icon: Package },
  { value: 'box', label: 'Box', icon: Package },
  { value: 'other', label: 'Other', icon: Package }
];

const PACKAGE_SIZES = [
  { value: 'small', label: 'Small (fits in hand)', description: 'Up to 1 lb' },
  { value: 'medium', label: 'Medium (shoebox size)', description: '1-10 lbs' },
  { value: 'large', label: 'Large (moving box)', description: '10-30 lbs' },
  { value: 'oversized', label: 'Oversized', description: '30+ lbs' }
];

const SPECIAL_HANDLING = [
  { value: 'fragile', label: 'Fragile', icon: AlertTriangle },
  { value: 'keep_upright', label: 'Keep Upright', icon: Package },
  { value: 'temperature_sensitive', label: 'Temperature Sensitive', icon: Package },
  { value: 'do_not_bend', label: 'Do Not Bend', icon: FileText }
];

const CourierBookingForm = ({ 
  service, 
  formData, 
  setFormData, 
  onSubmit, 
  onBack,
  user,
  isSubmitting 
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Default courier info values
  const COURIER_DEFAULTS = {
    // Pickup
    pickup_contact_is_me: true,
    pickup_contact_name: user?.full_name || '',
    pickup_contact_phone: user?.phone || '',
    pickup_company: '',
    pickup_address: '',
    pickup_city: '',
    pickup_state: '',
    pickup_zip: '',
    pickup_date: formData.scheduled_date || '',
    pickup_time: formData.scheduled_time || '',
    pickup_instructions: '',
    pickup_location_notes: '',
    
    // Delivery
    recipient_is_me: false,
    recipient_name: '',
    recipient_company: '',
    recipient_phone: '',
    delivery_address: '',
    delivery_city: '',
    delivery_state: '',
    delivery_zip: '',
    delivery_date: '',
    delivery_time_window: '',
    delivery_instructions: '',
    delivery_location_notes: '',
    
    // Package
    package_type: 'package',
    package_description: '',
    num_packages: 1,
    package_size: 'medium',
    estimated_weight: '',
    special_handling: [],
    prohibited_items_confirmed: false,
    
    // Photos
    package_photos: [],
    pickup_documents: [],
    
    // Options
    signature_required: false,
    is_rush: false,
    rush_fee: 0,
    num_stops: 1,
    additional_stops: [],
    
    // Estimate
    estimated_distance: 0,
    estimated_travel_time: 0,
    estimated_total: 0
  };
  
  // Initialize courier_info by merging defaults with any existing values
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      courier_info: {
        ...COURIER_DEFAULTS,
        ...(prev.courier_info || {}),
        // Ensure user contact info is set for pickup if pickup_contact_is_me
        pickup_contact_name: prev.courier_info?.pickup_contact_is_me !== false 
          ? (user?.full_name || prev.courier_info?.pickup_contact_name || '') 
          : (prev.courier_info?.pickup_contact_name || ''),
        pickup_contact_phone: prev.courier_info?.pickup_contact_is_me !== false 
          ? (user?.phone || prev.courier_info?.pickup_contact_phone || '') 
          : (prev.courier_info?.pickup_contact_phone || '')
      }
    }));
  }, [user?.full_name, user?.phone]);

  const courierInfo = formData.courier_info || {};
  
  const updateCourierInfo = (field, value) => {
    setFormData(prev => ({
      ...prev,
      courier_info: {
        ...prev.courier_info,
        [field]: value
      }
    }));
  };

  const updateMultipleCourierFields = (fields) => {
    setFormData(prev => ({
      ...prev,
      courier_info: {
        ...prev.courier_info,
        ...fields
      }
    }));
  };

  // Calculate estimate when addresses change
  const calculateEstimate = useCallback(async () => {
    if (!courierInfo.pickup_zip || !courierInfo.delivery_zip) return;
    
    setEstimating(true);
    try {
      // Calculate distance
      const distanceRes = await mileageAPI.calculate(courierInfo.pickup_zip, courierInfo.delivery_zip);
      const distance = distanceRes.data?.distance_miles || 0;
      const travelTime = Math.ceil(distance * 2.5); // Rough estimate: 2.5 min per mile
      
      // Base price calculation
      let basePrice = service?.hourly_rate || 25;
      let distanceFee = distance > 15 ? (distance - 15) * 0.65 : 0;
      let rushFee = courierInfo.is_rush ? 15 : 0;
      let stopsFee = (courierInfo.num_stops - 1) * 10;
      let sizeFee = courierInfo.package_size === 'large' ? 10 : courierInfo.package_size === 'oversized' ? 25 : 0;
      let handlingFee = (courierInfo.special_handling || []).length * 5;
      
      const total = basePrice + distanceFee + rushFee + stopsFee + sizeFee + handlingFee;
      
      setEstimate({
        distance,
        travelTime,
        basePrice,
        distanceFee,
        rushFee,
        stopsFee,
        sizeFee,
        handlingFee,
        total
      });
      
      updateMultipleCourierFields({
        estimated_distance: distance,
        estimated_travel_time: travelTime,
        estimated_total: total
      });
      
    } catch (error) {
      console.error('Estimate calculation error:', error);
      toast.error('Could not calculate estimate');
    } finally {
      setEstimating(false);
    }
  }, [courierInfo.pickup_zip, courierInfo.delivery_zip, courierInfo.is_rush, courierInfo.num_stops, courierInfo.package_size, courierInfo.special_handling, service]);

  // Handle photo upload
  const handlePhotoUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingPhoto(true);
    try {
      // For now, create object URLs - in production would upload to storage
      const photoUrls = files.map(f => URL.createObjectURL(f));
      
      if (type === 'package') {
        updateCourierInfo('package_photos', [...(courierInfo.package_photos || []), ...photoUrls]);
      } else {
        updateCourierInfo('pickup_documents', [...(courierInfo.pickup_documents || []), ...photoUrls]);
      }
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to add photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = (type, index) => {
    if (type === 'package') {
      const updated = [...(courierInfo.package_photos || [])];
      updated.splice(index, 1);
      updateCourierInfo('package_photos', updated);
    } else {
      const updated = [...(courierInfo.pickup_documents || [])];
      updated.splice(index, 1);
      updateCourierInfo('pickup_documents', updated);
    }
  };

  // Add additional stop
  const addStop = () => {
    const currentStops = courierInfo.additional_stops || [];
    if (currentStops.length < 5) {
      updateMultipleCourierFields({
        num_stops: courierInfo.num_stops + 1,
        additional_stops: [...currentStops, {
          address: '',
          city: '',
          state: '',
          zip: '',
          contact_name: '',
          contact_phone: '',
          instructions: ''
        }]
      });
    }
  };

  const removeStop = (index) => {
    const currentStops = [...(courierInfo.additional_stops || [])];
    currentStops.splice(index, 1);
    updateMultipleCourierFields({
      num_stops: courierInfo.num_stops - 1,
      additional_stops: currentStops
    });
  };

  const updateStop = (index, field, value) => {
    const currentStops = [...(courierInfo.additional_stops || [])];
    currentStops[index] = { ...currentStops[index], [field]: value };
    updateCourierInfo('additional_stops', currentStops);
  };

  // Step validation
  const canProceed = () => {
    switch (currentStep) {
      case 1: // Pickup
        return courierInfo.pickup_contact_name && 
               courierInfo.pickup_contact_phone && 
               courierInfo.pickup_address && 
               courierInfo.pickup_zip &&
               courierInfo.pickup_date &&
               courierInfo.pickup_time;
      case 2: // Delivery
        return courierInfo.recipient_name && 
               courierInfo.recipient_phone && 
               courierInfo.delivery_address && 
               courierInfo.delivery_zip;
      case 3: // Package
        return courierInfo.package_type && 
               courierInfo.num_packages >= 1 &&
               courierInfo.prohibited_items_confirmed;
      case 4: // Options
        return true;
      case 5: // Review
        return estimate && estimate.total > 0;
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!canProceed()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (currentStep === 4) {
      // Calculate estimate before going to review
      await calculateEstimate();
    }
    
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      onBack?.();
    }
  };

  const handleSubmit = () => {
    // Update main form data with courier estimate
    setFormData(prev => ({
      ...prev,
      scheduled_date: courierInfo.pickup_date,
      scheduled_time: courierInfo.pickup_time,
      address: courierInfo.pickup_address,
      city: courierInfo.pickup_city,
      state: courierInfo.pickup_state,
      zip_code: courierInfo.pickup_zip,
      total_amount: estimate?.total || 0,
      special_instructions: `COURIER: Pickup from ${courierInfo.pickup_address} to ${courierInfo.delivery_address}. ${courierInfo.num_packages} package(s). ${courierInfo.signature_required ? 'Signature required.' : ''}`
    }));
    
    onSubmit?.();
  };

  // Step Components
  const PickupStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <MapPin className="w-5 h-5 text-[var(--primary)]" />
        Pickup Information
      </h3>
      
      {/* Pickup Contact Toggle */}
      <div className="flex items-center gap-3 p-3 bg-[var(--background-subtle)] rounded-lg">
        <Checkbox
          id="pickup_is_me"
          checked={courierInfo.pickup_contact_is_me}
          onCheckedChange={(checked) => {
            updateMultipleCourierFields({
              pickup_contact_is_me: checked,
              pickup_contact_name: checked ? (user?.full_name || '') : '',
              pickup_contact_phone: checked ? (user?.phone || '') : ''
            });
          }}
        />
        <Label htmlFor="pickup_is_me" className="cursor-pointer">Pickup contact is me</Label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Contact Name *</Label>
          <Input
            value={courierInfo.pickup_contact_name || ''}
            onChange={(e) => updateCourierInfo('pickup_contact_name', e.target.value)}
            placeholder="John Smith"
            disabled={courierInfo.pickup_contact_is_me}
          />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input
            value={courierInfo.pickup_contact_phone || ''}
            onChange={(e) => updateCourierInfo('pickup_contact_phone', e.target.value)}
            placeholder="(555) 123-4567"
            disabled={courierInfo.pickup_contact_is_me}
          />
        </div>
      </div>

      <div>
        <Label>Company (Optional)</Label>
        <Input
          value={courierInfo.pickup_company || ''}
          onChange={(e) => updateCourierInfo('pickup_company', e.target.value)}
          placeholder="Company name"
        />
      </div>

      <div>
        <Label>Pickup Address *</Label>
        <Input
          value={courierInfo.pickup_address || ''}
          onChange={(e) => updateCourierInfo('pickup_address', e.target.value)}
          placeholder="123 Main Street"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="col-span-2 md:col-span-1">
          <Label>City *</Label>
          <Input
            value={courierInfo.pickup_city || ''}
            onChange={(e) => updateCourierInfo('pickup_city', e.target.value)}
            placeholder="City"
          />
        </div>
        <div>
          <Label>State *</Label>
          <Select value={courierInfo.pickup_state || ''} onValueChange={(v) => updateCourierInfo('pickup_state', v)}>
            <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NC">NC</SelectItem>
              <SelectItem value="VA">VA</SelectItem>
              <SelectItem value="SC">SC</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>ZIP *</Label>
          <Input
            value={courierInfo.pickup_zip || ''}
            onChange={(e) => updateCourierInfo('pickup_zip', e.target.value)}
            placeholder="27801"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Pickup Date *</Label>
          <Input
            type="date"
            value={courierInfo.pickup_date || ''}
            onChange={(e) => updateCourierInfo('pickup_date', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
          />
        </div>
        <div>
          <Label>Pickup Time *</Label>
          <Input
            type="time"
            value={courierInfo.pickup_time || ''}
            onChange={(e) => updateCourierInfo('pickup_time', e.target.value)}
          />
        </div>
      </div>

      <div>
        <Label>Pickup Instructions</Label>
        <Textarea
          value={courierInfo.pickup_instructions || ''}
          onChange={(e) => updateCourierInfo('pickup_instructions', e.target.value)}
          placeholder="Ring doorbell, ask for reception, etc."
          rows={2}
        />
      </div>

      <div>
        <Label>Location Notes</Label>
        <Input
          value={courierInfo.pickup_location_notes || ''}
          onChange={(e) => updateCourierInfo('pickup_location_notes', e.target.value)}
          placeholder="Building B, Suite 200, use side entrance"
        />
      </div>
    </div>
  );

  const DeliveryStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <Truck className="w-5 h-5 text-[var(--primary)]" />
        Delivery Information
      </h3>
      
      {/* Recipient Toggle */}
      <div className="flex items-center gap-3 p-3 bg-[var(--background-subtle)] rounded-lg">
        <Checkbox
          id="recipient_is_me"
          checked={courierInfo.recipient_is_me}
          onCheckedChange={(checked) => {
            updateMultipleCourierFields({
              recipient_is_me: checked,
              recipient_name: checked ? (user?.full_name || '') : '',
              recipient_phone: checked ? (user?.phone || '') : ''
            });
          }}
        />
        <Label htmlFor="recipient_is_me" className="cursor-pointer">Recipient is me</Label>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Recipient Name *</Label>
          <Input
            value={courierInfo.recipient_name || ''}
            onChange={(e) => updateCourierInfo('recipient_name', e.target.value)}
            placeholder="Jane Doe"
            disabled={courierInfo.recipient_is_me}
          />
        </div>
        <div>
          <Label>Phone *</Label>
          <Input
            value={courierInfo.recipient_phone || ''}
            onChange={(e) => updateCourierInfo('recipient_phone', e.target.value)}
            placeholder="(555) 123-4567"
            disabled={courierInfo.recipient_is_me}
          />
        </div>
      </div>

      <div>
        <Label>Company (Optional)</Label>
        <Input
          value={courierInfo.recipient_company || ''}
          onChange={(e) => updateCourierInfo('recipient_company', e.target.value)}
          placeholder="Company name"
        />
      </div>

      <div>
        <Label>Delivery Address *</Label>
        <Input
          value={courierInfo.delivery_address || ''}
          onChange={(e) => updateCourierInfo('delivery_address', e.target.value)}
          placeholder="456 Oak Avenue"
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="col-span-2 md:col-span-1">
          <Label>City *</Label>
          <Input
            value={courierInfo.delivery_city || ''}
            onChange={(e) => updateCourierInfo('delivery_city', e.target.value)}
            placeholder="City"
          />
        </div>
        <div>
          <Label>State *</Label>
          <Select value={courierInfo.delivery_state || ''} onValueChange={(v) => updateCourierInfo('delivery_state', v)}>
            <SelectTrigger><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="NC">NC</SelectItem>
              <SelectItem value="VA">VA</SelectItem>
              <SelectItem value="SC">SC</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>ZIP *</Label>
          <Input
            value={courierInfo.delivery_zip || ''}
            onChange={(e) => updateCourierInfo('delivery_zip', e.target.value)}
            placeholder="27801"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label>Preferred Delivery Date</Label>
          <Input
            type="date"
            value={courierInfo.delivery_date || ''}
            onChange={(e) => updateCourierInfo('delivery_date', e.target.value)}
            min={courierInfo.pickup_date || new Date().toISOString().split('T')[0]}
          />
        </div>
        <div>
          <Label>Time Window</Label>
          <Select value={courierInfo.delivery_time_window || ''} onValueChange={(v) => updateCourierInfo('delivery_time_window', v)}>
            <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="morning">Morning (8am-12pm)</SelectItem>
              <SelectItem value="afternoon">Afternoon (12pm-5pm)</SelectItem>
              <SelectItem value="evening">Evening (5pm-8pm)</SelectItem>
              <SelectItem value="asap">As Soon As Possible</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label>Delivery Instructions</Label>
        <Textarea
          value={courierInfo.delivery_instructions || ''}
          onChange={(e) => updateCourierInfo('delivery_instructions', e.target.value)}
          placeholder="Leave at door, hand to recipient only, etc."
          rows={2}
        />
      </div>

      <div>
        <Label>Location Notes</Label>
        <Input
          value={courierInfo.delivery_location_notes || ''}
          onChange={(e) => updateCourierInfo('delivery_location_notes', e.target.value)}
          placeholder="Gate code: 1234, use back entrance"
        />
      </div>
    </div>
  );

  const PackageStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold flex items-center gap-2">
        <Package className="w-5 h-5 text-[var(--primary)]" />
        Package Information
      </h3>

      {/* Package Type Selection */}
      <div>
        <Label className="mb-2 block">What are you sending? *</Label>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
          {PACKAGE_TYPES.map(type => (
            <button
              key={type.value}
              type="button"
              onClick={() => updateCourierInfo('package_type', type.value)}
              className={`p-3 rounded-lg border-2 text-center transition-all ${
                courierInfo.package_type === type.value 
                  ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                  : 'border-[var(--border-primary)] hover:border-[var(--primary)]/50'
              }`}
            >
              <type.icon className={`w-5 h-5 mx-auto mb-1 ${courierInfo.package_type === type.value ? 'text-[var(--primary)]' : 'text-[var(--text-muted)]'}`} />
              <span className="text-xs font-medium">{type.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Package Count */}
      <div>
        <Label>Number of Packages *</Label>
        <div className="flex items-center gap-3 mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => updateCourierInfo('num_packages', Math.max(1, (courierInfo.num_packages || 1) - 1))}
            disabled={courierInfo.num_packages <= 1}
          >
            <Minus className="w-4 h-4" />
          </Button>
          <span className="text-xl font-bold w-12 text-center">{courierInfo.num_packages || 1}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => updateCourierInfo('num_packages', (courierInfo.num_packages || 1) + 1)}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Package Size */}
      <div>
        <Label className="mb-2 block">Approximate Size *</Label>
        <div className="space-y-2">
          {PACKAGE_SIZES.map(size => (
            <label
              key={size.value}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                courierInfo.package_size === size.value 
                  ? 'border-[var(--primary)] bg-[var(--primary)]/10' 
                  : 'border-[var(--border-primary)] hover:border-[var(--primary)]/50'
              }`}
            >
              <input
                type="radio"
                name="package_size"
                value={size.value}
                checked={courierInfo.package_size === size.value}
                onChange={(e) => updateCourierInfo('package_size', e.target.value)}
                className="sr-only"
              />
              <div className="flex-1">
                <p className="font-medium">{size.label}</p>
                <p className="text-xs text-[var(--text-muted)]">{size.description}</p>
              </div>
              {courierInfo.package_size === size.value && (
                <Check className="w-5 h-5 text-[var(--primary)]" />
              )}
            </label>
          ))}
        </div>
      </div>

      {/* Weight */}
      <div>
        <Label>Approximate Weight (lbs)</Label>
        <Input
          type="number"
          value={courierInfo.estimated_weight || ''}
          onChange={(e) => updateCourierInfo('estimated_weight', e.target.value)}
          placeholder="e.g., 5"
          min="0"
        />
      </div>

      {/* Special Handling */}
      <div>
        <Label className="mb-2 block">Special Handling Instructions</Label>
        <div className="grid grid-cols-2 gap-2">
          {SPECIAL_HANDLING.map(handling => (
            <label
              key={handling.value}
              className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                (courierInfo.special_handling || []).includes(handling.value)
                  ? 'border-[var(--warning)] bg-[var(--warning)]/10'
                  : 'border-[var(--border-primary)] hover:border-[var(--warning)]/50'
              }`}
            >
              <Checkbox
                checked={(courierInfo.special_handling || []).includes(handling.value)}
                onCheckedChange={(checked) => {
                  const current = courierInfo.special_handling || [];
                  if (checked) {
                    updateCourierInfo('special_handling', [...current, handling.value]);
                  } else {
                    updateCourierInfo('special_handling', current.filter(h => h !== handling.value));
                  }
                }}
              />
              <span className="text-sm font-medium">{handling.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Package Description */}
      <div>
        <Label>Package Description</Label>
        <Textarea
          value={courierInfo.package_description || ''}
          onChange={(e) => updateCourierInfo('package_description', e.target.value)}
          placeholder="Describe the contents (e.g., legal documents, electronics, clothing)"
          rows={2}
        />
      </div>

      {/* Package Photos */}
      <div>
        <Label className="mb-2 block">Package Photos (Optional)</Label>
        <div className="flex flex-wrap gap-2">
          {(courierInfo.package_photos || []).map((photo, idx) => (
            <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border">
              <img src={photo} alt={`Package ${idx + 1}`} className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => removePhoto('package', idx)}
                className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <label className="w-20 h-20 border-2 border-dashed border-[var(--border-primary)] rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-[var(--primary)] transition-colors">
            <Camera className="w-5 h-5 text-[var(--text-muted)]" />
            <span className="text-[10px] text-[var(--text-muted)] mt-1">Add Photo</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handlePhotoUpload(e, 'package')}
              className="hidden"
            />
          </label>
        </div>
      </div>

      {/* Prohibited Items Acknowledgment */}
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Checkbox
            id="prohibited_items"
            checked={courierInfo.prohibited_items_confirmed}
            onCheckedChange={(checked) => updateCourierInfo('prohibited_items_confirmed', checked)}
          />
          <div>
            <Label htmlFor="prohibited_items" className="cursor-pointer text-red-800 font-medium">
              I confirm this package does not contain prohibited items *
            </Label>
            <p className="text-xs text-red-600 mt-1">
              Prohibited: Hazardous materials, weapons, illegal substances, perishable food (unless pre-arranged), live animals
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const OptionsStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold">Delivery Options</h3>

      {/* Signature Required */}
      <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Pen className="w-5 h-5 text-[var(--primary)]" />
            <div>
              <p className="font-medium">Require Recipient Signature</p>
              <p className="text-xs text-[var(--text-muted)]">Recipient must sign upon delivery</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => updateCourierInfo('signature_required', !courierInfo.signature_required)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              courierInfo.signature_required ? 'bg-[var(--primary)]' : 'bg-gray-300'
            }`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              courierInfo.signature_required ? 'left-7' : 'left-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Rush Delivery */}
      <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className="w-5 h-5 text-amber-500" />
            <div>
              <p className="font-medium">Rush Delivery</p>
              <p className="text-xs text-[var(--text-muted)]">Priority handling +$15</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => updateCourierInfo('is_rush', !courierInfo.is_rush)}
            className={`relative w-12 h-6 rounded-full transition-colors ${
              courierInfo.is_rush ? 'bg-amber-500' : 'bg-gray-300'
            }`}
          >
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
              courierInfo.is_rush ? 'left-7' : 'left-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Multiple Stops */}
      <div className="p-4 border border-[var(--border-primary)] rounded-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Navigation className="w-5 h-5 text-[var(--primary)]" />
            <div>
              <p className="font-medium">Multiple Stops</p>
              <p className="text-xs text-[var(--text-muted)]">Add intermediate stops (+$10 each)</p>
            </div>
          </div>
          {courierInfo.num_stops < 6 && (
            <Button type="button" variant="outline" size="sm" onClick={addStop}>
              <Plus className="w-4 h-4 mr-1" /> Add Stop
            </Button>
          )}
        </div>

        {(courierInfo.additional_stops || []).length > 0 && (
          <div className="space-y-4 mt-4 pt-4 border-t border-[var(--border-primary)]">
            {(courierInfo.additional_stops || []).map((stop, idx) => (
              <div key={idx} className="p-3 bg-[var(--background-subtle)] rounded-lg relative">
                <button
                  type="button"
                  onClick={() => removeStop(idx)}
                  className="absolute top-2 right-2 text-red-500 hover:text-red-700"
                >
                  <X className="w-4 h-4" />
                </button>
                <p className="font-medium text-sm mb-2">Stop {idx + 1}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input
                    placeholder="Address"
                    value={stop.address || ''}
                    onChange={(e) => updateStop(idx, 'address', e.target.value)}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Contact Name"
                    value={stop.contact_name || ''}
                    onChange={(e) => updateStop(idx, 'contact_name', e.target.value)}
                    className="text-sm"
                  />
                  <Input
                    placeholder="Phone"
                    value={stop.contact_phone || ''}
                    onChange={(e) => updateStop(idx, 'contact_phone', e.target.value)}
                    className="text-sm"
                  />
                  <Input
                    placeholder="ZIP Code"
                    value={stop.zip || ''}
                    onChange={(e) => updateStop(idx, 'zip', e.target.value)}
                    className="text-sm"
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const ReviewStep = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-bold">Review Your Courier Booking</h3>

      {estimating ? (
        <div className="text-center py-8">
          <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-2" />
          <p className="text-[var(--text-muted)]">Calculating estimate...</p>
        </div>
      ) : (
        <>
          {/* Pickup Summary */}
          <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="w-4 h-4 text-green-500" />
              <span className="font-semibold text-sm">PICKUP</span>
            </div>
            <p className="font-medium">{courierInfo.pickup_contact_name}</p>
            <p className="text-sm text-[var(--text-muted)]">{courierInfo.pickup_address}</p>
            <p className="text-sm text-[var(--text-muted)]">{courierInfo.pickup_city}, {courierInfo.pickup_state} {courierInfo.pickup_zip}</p>
            <p className="text-sm mt-2">
              <Calendar className="w-3 h-3 inline mr-1" />
              {courierInfo.pickup_date} at {courierInfo.pickup_time}
            </p>
          </div>

          {/* Additional Stops */}
          {(courierInfo.additional_stops || []).map((stop, idx) => (
            <div key={idx} className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Navigation className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-sm">STOP {idx + 1}</span>
              </div>
              <p className="text-sm">{stop.address || 'Address not specified'}</p>
              {stop.contact_name && <p className="text-sm text-[var(--text-muted)]">{stop.contact_name}</p>}
            </div>
          ))}

          {/* Delivery Summary */}
          <div className="p-4 bg-[var(--background-subtle)] rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Truck className="w-4 h-4 text-red-500" />
              <span className="font-semibold text-sm">DELIVERY</span>
            </div>
            <p className="font-medium">{courierInfo.recipient_name}</p>
            <p className="text-sm text-[var(--text-muted)]">{courierInfo.delivery_address}</p>
            <p className="text-sm text-[var(--text-muted)]">{courierInfo.delivery_city}, {courierInfo.delivery_state} {courierInfo.delivery_zip}</p>
          </div>

          {/* Package Summary */}
          <div className="p-4 border border-[var(--border-primary)] rounded-lg">
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-4 h-4 text-[var(--primary)]" />
              <span className="font-semibold text-sm">PACKAGE</span>
            </div>
            <p className="text-sm"><strong>Type:</strong> {courierInfo.package_type}</p>
            <p className="text-sm"><strong>Quantity:</strong> {courierInfo.num_packages} package(s)</p>
            <p className="text-sm"><strong>Size:</strong> {courierInfo.package_size}</p>
            {courierInfo.special_handling?.length > 0 && (
              <p className="text-sm"><strong>Special:</strong> {courierInfo.special_handling.join(', ')}</p>
            )}
          </div>

          {/* Options Summary */}
          <div className="flex flex-wrap gap-2">
            {courierInfo.signature_required && (
              <span className="px-3 py-1 bg-[var(--primary)]/10 text-[var(--primary)] rounded-full text-sm font-medium flex items-center gap-1">
                <Pen className="w-3 h-3" /> Signature Required
              </span>
            )}
            {courierInfo.is_rush && (
              <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium flex items-center gap-1">
                <Zap className="w-3 h-3" /> Rush Delivery
              </span>
            )}
            {courierInfo.num_stops > 1 && (
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium flex items-center gap-1">
                <Navigation className="w-3 h-3" /> {courierInfo.num_stops} Stops
              </span>
            )}
          </div>

          {/* Price Estimate */}
          {estimate && (
            <div className="p-4 bg-gradient-to-r from-[var(--primary)]/10 to-[var(--secondary)]/10 rounded-xl">
              <h4 className="font-bold mb-3 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[var(--primary)]" />
                Price Estimate
              </h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Base Courier Price</span>
                  <span>${estimate.basePrice?.toFixed(2)}</span>
                </div>
                {estimate.distanceFee > 0 && (
                  <div className="flex justify-between text-[var(--text-muted)]">
                    <span>Distance ({estimate.distance?.toFixed(1)} mi)</span>
                    <span>+${estimate.distanceFee?.toFixed(2)}</span>
                  </div>
                )}
                {estimate.stopsFee > 0 && (
                  <div className="flex justify-between text-[var(--text-muted)]">
                    <span>Additional Stops ({courierInfo.num_stops - 1})</span>
                    <span>+${estimate.stopsFee?.toFixed(2)}</span>
                  </div>
                )}
                {estimate.sizeFee > 0 && (
                  <div className="flex justify-between text-[var(--text-muted)]">
                    <span>Size Surcharge</span>
                    <span>+${estimate.sizeFee?.toFixed(2)}</span>
                  </div>
                )}
                {estimate.handlingFee > 0 && (
                  <div className="flex justify-between text-[var(--text-muted)]">
                    <span>Special Handling</span>
                    <span>+${estimate.handlingFee?.toFixed(2)}</span>
                  </div>
                )}
                {estimate.rushFee > 0 && (
                  <div className="flex justify-between text-amber-600">
                    <span>Rush Fee</span>
                    <span>+${estimate.rushFee?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-[var(--border-primary)] font-bold text-lg">
                  <span>Total</span>
                  <span className="text-[var(--primary)]">${estimate.total?.toFixed(2)}</span>
                </div>
              </div>
              
              <div className="mt-3 pt-3 border-t border-[var(--border-primary)]">
                <p className="text-xs text-[var(--text-muted)] flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Estimated travel: {estimate.travelTime} minutes ({estimate.distance?.toFixed(1)} miles)
                </p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const steps = [
    { num: 1, label: 'Pickup', component: PickupStep },
    { num: 2, label: 'Delivery', component: DeliveryStep },
    { num: 3, label: 'Package', component: PackageStep },
    { num: 4, label: 'Options', component: OptionsStep },
    { num: 5, label: 'Review', component: ReviewStep }
  ];

  const CurrentStepComponent = steps.find(s => s.num === currentStep)?.component || PickupStep;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-6 px-2">
        {steps.map((s, idx) => (
          <React.Fragment key={s.num}>
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm transition-colors ${
                currentStep >= s.num 
                  ? 'bg-[var(--primary)] text-black' 
                  : 'bg-[var(--background-subtle)] text-[var(--text-muted)]'
              }`}>
                {currentStep > s.num ? <Check className="w-4 h-4" /> : s.num}
              </div>
              <span className="text-[10px] mt-1 text-[var(--text-muted)] hidden md:block">{s.label}</span>
            </div>
            {idx < steps.length - 1 && (
              <div className={`flex-1 h-1 mx-1 rounded ${
                currentStep > s.num ? 'bg-[var(--primary)]' : 'bg-[var(--background-subtle)]'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentStep}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="min-h-[400px]"
        >
          <CurrentStepComponent />
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6 pt-4 border-t border-[var(--border-primary)]">
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          className="flex items-center gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </Button>

        {currentStep < 5 ? (
          <Button
            type="button"
            onClick={handleNext}
            disabled={!canProceed() || estimating}
            className="flex items-center gap-2 bg-[var(--primary)] text-black hover:bg-[var(--primary-dark)]"
          >
            {currentStep === 4 ? 'Calculate Price' : 'Next'}
            <ChevronRight className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || !estimate}
            className="flex items-center gap-2 bg-[var(--primary)] text-black hover:bg-[var(--primary-dark)]"
          >
            {isSubmitting ? 'Processing...' : 'Confirm & Pay'}
            <Check className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default CourierBookingForm;
